/**
 * Guide engine v3 overlay - rings layer + reveal (#1025, epic #1020). Spec R15.
 *
 * Renders one GuideRing per highlight, in array order. When a step has several
 * highlights they light up ONE AT A TIME, ~500ms apart (a "Wheel of Fortune"
 * sequential reveal), then all settle into the pulse. Reduced motion = every ring
 * appears at once, static (no stagger). The reveal is cosmetic: every element is
 * interactive throughout (GuideRing is pointer-events:none).
 *
 * Mounted per step (keyed by the runtime), so the reveal restarts each step.
 */

import { useEffect, useState } from 'react';
import GuideRing from './GuideRing.jsx';
import { useResolvedReducedMotion } from './motion';

const DEFAULT_STAGGER_MS = 500;

const GuideRings = ({ highlights = [], staggerMs = DEFAULT_STAGGER_MS, reducedMotion, zIndex }) => {
  const reduced = useResolvedReducedMotion(reducedMotion);
  const count = highlights.length;
  // How many rings have lit so far. Reduced motion reveals all at once; otherwise
  // the first lights immediately and the rest follow one per `staggerMs`.
  const [revealed, setRevealed] = useState(() => (reduced || count <= 1 ? count : 1));

  useEffect(() => {
    if (reduced || count <= 1) {
      setRevealed(count);
      return undefined;
    }
    setRevealed(1);
    const timer = setInterval(() => {
      setRevealed((n) => {
        if (n >= count) {
          clearInterval(timer);
          return n;
        }
        return n + 1;
      });
    }, staggerMs);
    return () => clearInterval(timer);
  }, [count, reduced, staggerMs]);

  if (count === 0) return null;

  return (
    <>
      {highlights.map((highlight, i) => (
        <GuideRing
          key={highlight.id != null ? highlight.id : i}
          highlight={highlight}
          visible={i < revealed}
          reducedMotion={reduced}
          zIndex={zIndex}
        />
      ))}
    </>
  );
};

export default GuideRings;
